/**
 * The route table.
 *
 * Slice 0 established sign in, set a password, and the authenticated shell.
 * Slice 1 adds the three screens the import conversation needs — the list of
 * uploads, the upload itself, and one dataset. Slice 2 adds the three that
 * combine those uploads into a segment. Slice 3 adds one more — the Integrations
 * screen, where a tenant connects their own Amazon SES account. Slice 4 adds two:
 * the list of email templates, and the editor with its preview. Slice 5 adds the
 * three that actually send: the campaign list, the one-page create screen, and
 * the campaign itself — which is where a campaign is scheduled, and the only
 * place in this app that can put a message in somebody's inbox. Slice 6 adds
 * one more: the Credits screen, where a tenant reads the balance a delivery
 * consumed and a bounce released, rather than guessing at either. Slice 13 adds
 * `/reports`, which is the same history asked as a question a spreadsheet can
 * answer — and, unlike every other screen here, one whose work outlives the
 * request that started it, because a run is a record rather than a response.
 * Slice 14 adds `/analytics`, which asks that same history the other way round:
 * not "put a period in a file" but "show me a period on screen, and how many
 * distinct people it reached".
 *
 * **`/analytics` takes no parameters, and the period is deliberately not in the
 * path.** A `/analytics/:from/:to` would put two dates in a URL with no timezone
 * attached, and a link pasted between two people in different zones would then
 * describe two different periods while looking identical — which is the exact
 * class of mistake this slice exists to close. A period here is two calendar
 * dates *and* the zone they are read in, so it travels as the query the screen
 * sends rather than as a route. The cost is that a period cannot be linked to,
 * and that stays the right trade until a link can carry the zone.
 *
 * **There is no `/reports/:runId`, and that is not an omission.** A run has no
 * detail worth a screen of its own: what it covered, what started it, how it
 * ended and its file all fit on its row, and a route that took a run id would be
 * a second place a tenant could ask for a run by guessing one.
 *
 * `/audiences/upload` is written before `/audiences/:datasetId`,
 * `/segments/new` before `/segments/:segmentId`, and `/campaigns/new` before
 * `/campaigns/:campaignId`, for reading order only. React Router ranks a static
 * segment above a dynamic one, so none could be swallowed as an id whatever the
 * order here.
 *
 * `/set-password` is reachable without a session by definition: it is where
 * someone goes to acquire one for the first time.
 *
 * **Slice 65 moved Wend's Meta ads to `/reach/ads…`, and nothing else.** The
 * old Reach split into Reach (paid ads on the workspace's own Meta ad account)
 * and Connect (email and WhatsApp campaigns). The ads had sat under
 * `/campaigns/ads` since slice 42 and, from slice 44, in a panel on the
 * campaigns page; they have a page of their own at `/reach/ads` now, with the
 * composer, a draft and its correction beneath it, and `/reach` lands on that
 * list — the pillar's only screen today. Every other address is unchanged on
 * purpose: campaign, audience, segment, template and Sense URLs are emitted by
 * the API (Today's and the Overview's hrefs), bookmarked, and asserted by the
 * suites, and renaming `/campaigns` to `/connect/campaigns` would have broken
 * all three to make a URL match a sidebar heading.
 *
 * The four old ad addresses stay declared and redirect, keeping their params,
 * query and fragment — see `routes/reach/MovedAdRoute.tsx` for why `Navigate`
 * alone could not. They are declared before `/campaigns/:campaignId`, so
 * `ads` is never read as a campaign id.
 */
import { Navigate, Route, Routes } from 'react-router-dom';

import { RedirectIfAuthenticated } from './auth/RedirectIfAuthenticated';
import { RequireAuth } from './auth/RequireAuth';
import { HomePage } from './routes/HomePage';
import { LoginPage } from './routes/LoginPage';
import { NotFoundPage } from './routes/NotFoundPage';
import { SetPasswordPage } from './routes/SetPasswordPage';
import { AnalyticsPage } from './routes/analytics/AnalyticsPage';
import { AudiencesPage } from './routes/audiences/AudiencesPage';
import { DatasetDetailPage } from './routes/audiences/DatasetDetailPage';
import { FieldsPage } from './routes/audiences/fields/FieldsPage';
import { UploadPage } from './routes/audiences/UploadPage';
import { CampaignDetailPage } from './routes/campaigns/CampaignDetailPage';
import { CampaignsPage } from './routes/campaigns/CampaignsPage';
import { NewCampaignPage } from './routes/campaigns/NewCampaignPage';
import { MetaAdComposerPage } from './routes/meta-ads/MetaAdComposerPage';
import { MetaAdDraftPage } from './routes/meta-ads/MetaAdDraftPage';
import { CreditsPage } from './routes/credits/CreditsPage';
import { FlowEditorPage } from './routes/flows/FlowEditorPage';
import { FlowsPage } from './routes/flows/FlowsPage';
import { NewFlowPage } from './routes/flows/NewFlowPage';
import { IntegrationsPage } from './routes/integrations/IntegrationsPage';
import { MovedAdRoute } from './routes/reach/MovedAdRoute';
import { ReachAdsPage } from './routes/reach/ReachAdsPage';
import { ReachLeadsPage } from './routes/reach/leads/ReachLeadsPage';
import { ReportsPage } from './routes/reports/ReportsPage';
import { NewSegmentPage } from './routes/segments/NewSegmentPage';
import { SegmentDetailPage } from './routes/segments/SegmentDetailPage';
import { SegmentsPage } from './routes/segments/SegmentsPage';
import { ActionLedgerPage } from './routes/sense/ActionLedgerPage';
import { FunnelPage } from './routes/sense/FunnelPage';
import { OutcomesPage } from './routes/sense/OutcomesPage';
import { PressurePage } from './routes/sense/PressurePage';
import { TodayPage } from './routes/sense/TodayPage';
import { TemplateEditorPage } from './routes/templates/TemplateEditorPage';
import { TemplatesPage } from './routes/templates/TemplatesPage';

export function App() {
  return (
    <Routes>
      <Route element={<RedirectIfAuthenticated />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      {/* The API mails `${APP_BASE_URL}/set-password?token=…`; the path form is
          accepted as well so a link written either way redeems. With no token
          at all the screen offers to send a fresh one. */}
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/set-password/:token" element={<SetPasswordPage />} />

      <Route element={<RequireAuth />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/audiences" element={<AudiencesPage />} />
        <Route path="/audiences/upload" element={<UploadPage />} />
        {/* Slice 67: the field catalogue. Static, so it ranks above /audiences/:datasetId. */}
        <Route path="/audiences/fields" element={<FieldsPage />} />
        <Route path="/audiences/:datasetId" element={<DatasetDetailPage />} />
        <Route path="/segments" element={<SegmentsPage />} />
        <Route path="/segments/new" element={<NewSegmentPage />} />
        <Route path="/segments/:segmentId" element={<SegmentDetailPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/templates/:templateId" element={<TemplateEditorPage />} />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/campaigns/new" element={<NewCampaignPage />} />
        {/* Slice 65: the Meta ads moved to Reach. Their slice 42 addresses redirect,
            params and query kept. Static segments, so they rank above /campaigns/:campaignId. */}
        <Route path="/campaigns/ads" element={<MovedAdRoute to="/reach/ads" />} />
        <Route path="/campaigns/ads/new" element={<MovedAdRoute to="/reach/ads/new" />} />
        <Route path="/campaigns/ads/:draftId" element={<MovedAdRoute to="/reach/ads/:draftId" />} />
        <Route path="/campaigns/ads/:draftId/edit" element={<MovedAdRoute to="/reach/ads/:draftId/edit" />} />
        <Route path="/campaigns/:campaignId" element={<CampaignDetailPage />} />
        {/* Reach (slice 65): Wend's ads on the workspace's Meta ad account. /reach lands on the list. */}
        <Route path="/reach" element={<Navigate to="/reach/ads" replace />} />
        <Route path="/reach/ads" element={<ReachAdsPage />} />
        <Route path="/reach/ads/new" element={<MetaAdComposerPage />} />
        <Route path="/reach/ads/:draftId" element={<MetaAdDraftPage />} />
        <Route path="/reach/ads/:draftId/edit" element={<MetaAdComposerPage />} />
        {/* Slice 70: the people Reach's ads brought in. */}
        <Route path="/reach/leads" element={<ReachLeadsPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/credits" element={<CreditsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        {/* Sense: Today (slice 62) is where /sense lands; Funnel, Pressure, Outcomes and the ledger beside it. */}
        <Route path="/sense" element={<Navigate to="/sense/today" replace />} />
        <Route path="/sense/today" element={<TodayPage />} />
        <Route path="/sense/actions" element={<ActionLedgerPage />} />
        <Route path="/sense/funnel" element={<FunnelPage />} />
        <Route path="/sense/pressure" element={<PressurePage />} />
        <Route path="/sense/outcomes" element={<OutcomesPage />} />
        {/* Flow (slice 69): the list, a new draft from a starting template, and the
            full-screen builder. /flows/new is static, so it ranks above /flows/:flowId. */}
        <Route path="/flows" element={<FlowsPage />} />
        <Route path="/flows/new" element={<NewFlowPage />} />
        <Route path="/flows/:flowId" element={<FlowEditorPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
